import React, { useMemo } from 'react';

/**
 * Slack/Teams-style initials avatar. Colour is deterministic from the
 * seed string (principalId, userId, or name) so the same participant
 * always gets the same colour across the UI. Seven preset colours
 * chosen to stay accessible on light and dark backgrounds.
 */
const PALETTE = [
	{ bg: '#DBEAFE', text: '#1E40AF' }, // blue
	{ bg: '#FCE7F3', text: '#9D174D' }, // pink
	{ bg: '#D1FAE5', text: '#065F46' }, // green
	{ bg: '#FEF3C7', text: '#92400E' }, // amber
	{ bg: '#E0E7FF', text: '#3730A3' }, // indigo
	{ bg: '#FEE2E2', text: '#991B1B' }, // red
	{ bg: '#F3E8FF', text: '#6B21A8' }, // purple
];

interface AvatarProps {
	name?: string;
	seed: string;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	online?: boolean;
	className?: string;
}

export const Avatar: React.FC<AvatarProps> = ({
	name,
	seed,
	size = 'md',
	online,
	className,
}) => {
	const { bg, text, initials } = useMemo(() => {
		const hash = hashString(seed);
		const palette = PALETTE[hash % PALETTE.length];
		return { ...palette, initials: initialsOf(name ?? seed) };
	}, [seed, name]);

	const sizes = {
		xs: {
			dim: 'h-6 w-6',
			text: 'text-[10px]',
			dot: 'h-1.5 w-1.5 -right-0 -bottom-0',
		},
		sm: { dim: 'h-7 w-7', text: 'text-xs', dot: 'h-2 w-2 -right-0 -bottom-0' },
		md: {
			dim: 'h-9 w-9',
			text: 'text-sm',
			dot: 'h-2.5 w-2.5 -right-0.5 -bottom-0.5',
		},
		lg: {
			dim: 'h-11 w-11',
			text: 'text-base',
			dot: 'h-3 w-3 -right-0.5 -bottom-0.5',
		},
	}[size];

	return (
		<span className={`relative inline-flex flex-shrink-0 ${className ?? ''}`}>
			<span
				className={`${sizes.dim} ${sizes.text} flex items-center justify-center rounded-full font-semibold`}
				style={{ backgroundColor: bg, color: text }}
			>
				{initials}
			</span>
			{online != null && (
				<span
					className={`absolute ${sizes.dot} rounded-full border-2`}
					style={{
						backgroundColor: online ? '#10B981' : '#9CA3AF',
						borderColor: 'var(--color-background, #FFFFFF)',
					}}
				/>
			)}
		</span>
	);
};

function initialsOf(s: string): string {
	const parts = s.split(/[\s:@._-]+/).filter((x) => x.length > 0);
	if (parts.length === 0) return '?';
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function hashString(s: string): number {
	let h = 0;
	for (let i = 0; i < s.length; i++) {
		h = (h << 5) - h + s.charCodeAt(i);
		h |= 0;
	}
	return Math.abs(h);
}
